import React from 'react';
import { Menu, Plus, RefreshCw, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useProjects } from '../../hooks/queries/useProjects';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';
import { logger } from '../../utils/logger';

interface TopicHeaderProps {
  onNewTopic: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onMenuClick: () => void;
  projectId: string;
  onCollapse?: () => void;
  hideSearchAndNewTopic?: boolean;
  isScrolled?: boolean;
  isBackgroundFetching?: boolean; // Added for React Query stale-while-revalidate indicator
}

export function TopicHeader({
  onNewTopic,
  searchQuery,
  onSearchChange,
  onMenuClick,
  projectId,
  hideSearchAndNewTopic = false,
  isScrolled = false,
  isBackgroundFetching = false,
}: TopicHeaderProps) {
  const { data: projects = [] } = useProjects();
  const activeProject = projects.find(p => p.id === projectId);

  // Get status bar information for mobile adjustments
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  // Log status bar info for debugging
  React.useEffect(() => {
    if (isNativeApp) {
      logger.capacitor('TopicHeader: Using status bar height', {
        statusBarHeight
      });
    }
  }, [isNativeApp, statusBarHeight]);

  return (
    <div
      className={`sticky top-0 z-50 transition-shadow duration-200 ${
        isScrolled ? 'shadow-md' : ''
      }`}
      style={{
        paddingTop: isNativeApp ? `${statusBarHeight}px` : '0',
      }}
    >
      <div className="flex items-end h-14 px-4 lg:hidden">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Menu size={24} />
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 mb-2">
          <span className="text-xl font-bold text-gray-900 truncate max-w-[200px]">
            {activeProject?.name || ''}
          </span>
        </div>
        
        {/* Background fetching indicator for mobile */}
        {isBackgroundFetching && (
          <div className="absolute right-4 mb-2">
            <RefreshCw size={18} className="text-blue-500 animate-spin opacity-70" />
          </div>
        )}
      </div>

      {/* Search Bar with New Topic Button - hidden in empty state */}
      {!hideSearchAndNewTopic && (
        <div className="p-4">
          <div className="relative flex items-center gap-4">
            <div className="flex-1 relative">
              <Search
                size={20}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={e => onSearchChange(e.target.value)}
                placeholder="Search topics..."
                className={cn(
                  'w-full pl-10 pr-4 py-2 bg-gray-50 rounded-lg text-base',
                  'outline-none',
                  'placeholder-gray-400'
                )}
              />
              
              {/* Background fetching indicator in search box (desktop only) */}
              {isBackgroundFetching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden lg:block">
                  <RefreshCw size={16} className="text-blue-500 animate-spin opacity-70" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-new-topic-button
                onClick={onNewTopic}
                className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 rounded-full transition-all shadow-sm"
                title="New topic"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
